import * as React from 'react';

import { Avatar, AvatarFallback } from '@cccom/ui/src/components/shadcn/avatar';
import { cn } from '@cccom/ui/src/utils';

const colorMap: { [key: string]: string } = {
  A: '#DD6B20',
  B: '#2D3748',
  C: '#718096',
  D: '#F56565',
  E: '#9B2C2C',
  F: '#63171B',
  G: '#ED8936',
  H: '#9C4221',
  I: '#652B19',
  J: '#ECC94B',
  K: '#975A16',
  L: '#5F370E',
  M: '#48BB78',
  N: '#276749',
  O: '#1C4532',
  P: '#38B2AC',
  Q: '#285E61',
  R: '#1D4044',
  S: '#4299E1',
  T: '#2C5282',
  U: '#1A365D',
  V: '#9F7AEA',
  W: '#553C9A',
  X: '#322659',
  Y: '#ED64A6',
  Z: '#97266D',
};

export function useGetNameColor(brandColor = 'bg-brand-300') {
  const getColor = (name?: string) => {
    if (!name) return brandColor;
    const firstLetter = name.charAt(0).toUpperCase();
    return colorMap[firstLetter] || brandColor;
  };
  return { getColor };
}

// Define size variants for the avatar
const sizeVariants = {
  xs: {
    avatar: 'h-6 w-6',
    text: 'text-xs',
  },
  sm: {
    avatar: 'h-7 w-7',
    text: 'text-xs',
  },
  md: {
    avatar: 'h-8 w-8',
    text: 'text-sm',
  },
  lg: {
    avatar: 'h-10 w-10',
    text: 'text-base',
  },
  xl: {
    avatar: 'h-12 w-12',
    text: 'text-lg',
  },
};

export type AvatarSize = keyof typeof sizeVariants;

export type CCAvatarProps = React.HTMLAttributes<HTMLDivElement> & {
  name: string;
  className?: string;
  size?: AvatarSize;
};

export function CCAvatar({
  name,
  className,
  size = 'md',
  ...props
}: CCAvatarProps) {
  const { getColor } = useGetNameColor();

  const getInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const bgColor = getColor(name);
  const sizeClasses = sizeVariants[size] || sizeVariants.md;

  return (
    <Avatar
      className={cn('rounded-full', sizeClasses.avatar, className)}
      data-testid="avatar"
      {...props}
    >
      <AvatarFallback
        className={cn('text-white', sizeClasses.text)}
        style={{ backgroundColor: bgColor }}
      >
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

export default CCAvatar;
